import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { useAuth } from './context/AuthContext';
import { usePricing } from './contexts/PricingContext';
import api from './api';

// Reusable PricingCard Component
const PricingCard = ({ plan, subtitle, price, promoPrice, planType, features, colors, onGetNow, isLoading, buttonText = "Start 7-Day Free Trial", discountPercent = null }) => {
  // Determine if there's an active discount
  const hasDiscount = promoPrice !== null && promoPrice !== undefined && parseFloat(promoPrice) !== parseFloat(price);
  const savings = hasDiscount ? Math.round((parseFloat(price) - parseFloat(promoPrice)) * 100) / 100 : 0;
  
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden relative">
      {/* Promotional Badge - Only show if there's an actual discount */}
      {hasDiscount && discountPercent && (
        <div className="absolute top-0 right-0 bg-red-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg z-10">
          SAVE {discountPercent}%
        </div>
      )}
      {/* Card Header */}
      <div className={`${colors.headerBg} px-6 py-4 text-center`}>
        <h3 className="text-white font-bold text-xl uppercase">{plan}</h3>
        <p className="text-white text-sm mt-1">{subtitle}</p>
      </div>

      {/* Price Section */}
      <div className="px-6 py-6 text-center">
        <div className="flex flex-col items-center">
          {hasDiscount ? (
            <>
              {/* Promotional Price (Main Display) */}
              <div className="flex items-baseline justify-center mb-2">
                <span className="text-gray-600 text-lg">₹</span>
                <span className="text-5xl font-bold text-gray-900 mx-1">{parseFloat(promoPrice).toFixed(0)}</span>
                <span className="text-gray-600 text-lg">.{(parseFloat(promoPrice) % 1).toFixed(2).split('.')[1]}</span>
              </div>
              {/* Original Price (Slashed) */}
              <div className="flex items-baseline justify-center mb-1">
                <span className="text-gray-500 text-lg">₹</span>
                <span className="text-xl font-semibold text-gray-500 line-through mx-1">{parseFloat(price).toFixed(0)}</span>
                <span className="text-gray-500 text-lg">.00</span>
              </div>
              {/* Savings Indicator */}
              <div className="text-green-600 text-sm font-semibold">
                You Save ₹{savings.toFixed(2)}
              </div>
            </>
          ) : (
            <>
              {/* No Discount - Show only original price */}
              <div className="flex items-baseline justify-center mb-2">
                <span className="text-gray-600 text-lg">₹</span>
                <span className="text-5xl font-bold text-gray-900 mx-1">{parseFloat(price).toFixed(0)}</span>
                <span className="text-gray-600 text-lg">.00</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Plan Type Banner */}
      <div className={`${colors.headerBg} px-6 py-2 text-center`}>
        <span className="text-white text-base font-bold uppercase tracking-widest">{planType}</span>
      </div>

      {/* Features List */}
      <div className="px-6 py-6">
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center border-b border-gray-100 pb-3 last:border-b-0">
              {feature.included ? (
                <FaCheckCircle className={`${colors.check} mr-3 flex-shrink-0`} />
              ) : (
                <FaTimesCircle className="text-gray-400 mr-3 flex-shrink-0" />
              )}
              <span className={`text-sm ${feature.included ? 'text-gray-800' : 'text-gray-500'}`}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA Button */}
      <div className="px-6 pb-6">
        <button 
          onClick={() => onGetNow(plan)}
          disabled={isLoading}
          className={`${colors.buttonBg} hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg w-full transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isLoading ? 'Processing...' : buttonText}
        </button>
      </div>
    </div>
  );
};

// PricingPage Component
const PricingPage = () => {
  const [searchParams] = useSearchParams();
  /** TrendVector mobile WebView (`tv_embed=1`): hide site chrome; back button is in the app. */
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  const [isMobile, setIsMobile] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const navigate = useNavigate();
  const { user, verifyPaymentAndRefresh, isAuthenticated, loading } = useAuth();
  const { activeOffer, plans: dynamicPlans, loading: pricingLoading, refreshPricing } = usePricing();

  // No redirect logic - all users can access pricing page
  // This allows active subscribers to renew/upgrade their plans anytime

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Refresh pricing data when page becomes visible (user switches back to tab)
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && refreshPricing) {
        console.log('📊 Page became visible, refreshing pricing data...');
        refreshPricing();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshPricing]);

  // Set trial status from user data (no API call needed)
  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if subscription is expired
      const isSubscriptionExpired = user.subscription_valid_until 
        ? new Date(user.subscription_valid_until) < new Date()
        : false;

      // Determine if we should show renew button
      // Show "Renew Plan" for any user who has used their trial
      // This includes active subscribers, expired subscribers, and users who used trial but never subscribed
      const shouldShowRenewButton = user.has_used_trial || user.subscription_plan;

      // Use user data directly - no need to fetch from backend
      // This works for both trial users and existing subscribers
      setTrialStatus({
        has_used_trial: user.has_used_trial || false,
        is_trial_active: false,
        can_start_trial: !user.has_used_trial && !user.subscription_plan,
        current_plan: user.subscription_plan,
        subscription_valid_until: user.subscription_valid_until,
        is_subscription_expired: isSubscriptionExpired,
        should_show_renew_button: shouldShowRenewButton
      });
    }
  }, [isAuthenticated, user]);

  // Function to determine button text based on trial status
  const getButtonText = (planName) => {
    // Use trialStatus if available, otherwise fall back to user data
    if (!trialStatus) {
      // Fallback: Use user data directly
      if (user?.has_used_trial || user?.subscription_plan) {
        // User has used trial or has/had a subscription
        if (user?.subscription_valid_until) {
          const isExpired = new Date(user.subscription_valid_until) < new Date();
          return isExpired ? "Restore Access" : "Renew Plan";
        }
        return "Choose Plan";
      }
      return "Start 7-Day Free Trial";
    }
    
    // User has used trial or has a subscription plan
    if (trialStatus.should_show_renew_button) {
      // User used trial but no active subscription
      if (trialStatus.has_used_trial && !trialStatus.subscription_valid_until) {
        return "Choose Plan";
      }
      // Subscription is expired
      if (trialStatus.is_subscription_expired) {
        return "Restore Access";
      }
      // Active subscription or trial used - show renew
      return "Renew Plan";
    }
    
    // New user who hasn't used trial
    return "Start 7-Day Free Trial";
  };

  // Handle payment function
  const handlePayment = async (planName) => {
    if (!user) {
      // console.log("🚫 PricingPage: No user, redirecting to login");
      navigate('/login');
      return;
    }

    setIsLoading(true);
    let paymentHandled = false;
    try {
      // Step A: Create order with correct plan identifier and price
      const orderResponse = await api.post('/api/v1/payments/create-order', {
        plan_name: planName
      });

      const { order_id, amount, key_id } = orderResponse.data;
      // LOG 1: Check the data received from your backend to create the order.
      // console.log("--- STEP 1: ORDER CREATED ---", { order_id, amount, key_id });

      // Step B: Open Razorpay modal
      const options = {
        key: key_id,
        amount: amount * 100, // Convert to paise
        currency: "INR",
        name: "TrendVector",
        description: `${planName} Plan Subscription`,
        order_id: order_id,
        handler: async function (response) {   
          paymentHandled = true;
          // LOG 2: Confirm the handler was called and see what Razorpay provided.
          // console.log("--- STEP 2: RAZORPAY HANDLER TRIGGERED ---", response);     

          try {
            // --- SIMPLIFIED PAYMENT HANDLER USING CENTRALIZED FUNCTION ---
            const verificationData = {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature
            };

            // Step C: Use centralized payment verification and refresh function
            // console.log("--- STEP 3: CALLING CENTRALIZED PAYMENT VERIFICATION ---");
            const result = await verifyPaymentAndRefresh(verificationData);

            if (result.success) {
              // LOG 4: Confirm the centralized function was successful.
              // console.log("--- STEP 4: CENTRALIZED PAYMENT VERIFICATION SUCCESSFUL ---");
              // console.log("📊 User state is now guaranteed to be updated:", result.data);

              // Step D: Double-check the subscription status before navigation
              // console.log("🔍 Double-checking subscription status before navigation...");
              // console.log("Current hasActiveSubscription:", hasActiveSubscription);
              // console.log("Current user subscription_plan:", user?.subscription_plan);
              // console.log("Current user subscription_valid_until:", user?.subscription_valid_until);

              // Step E: Navigate to dashboard with confidence
              // Because we awaited verifyPaymentAndRefresh, the user state is now fresh.
              // console.log("--- STEP 5: NAVIGATING TO DASHBOARD WITH UPDATED STATE ---");
              
              // Check if user is on trial and show appropriate message
              const isTrialUser = result.data?.subscription_plan === 'EXPERT_TRIAL';
              const message = isTrialUser 
                ? "Welcome to your 7-day EXPERT trial! Your subscription is now active with full access to all features."
                : `Payment successful! Your ${planName} plan has been activated.`;
              
              navigate('/dashboard', { 
                state: { 
                  message: message
                } 
              });
            } else {
              // LOG 5: Handle payment verification failure
              console.error("--- PAYMENT VERIFICATION FAILED ---");
              console.error("Error:", result.error);
              alert(result.error);
            }
          } catch (error) {
            // LOG 6: Handle any unexpected errors
            console.error("--- UNEXPECTED ERROR IN PAYMENT HANDLER ---");
            console.error("Error during payment verification:", error);
            alert('An unexpected error occurred. Please contact support.');
          }
        },
        prefill: {
          name: user.first_name || user.email,
          email: user.email,
        },
        theme: {
          color: "#1D3557"
        },
        modal: {
          ondismiss: function() {
            setIsLoading(false);
            if (!paymentHandled) {
              alert('Payment declined. You need to try again.');
            }
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Payment initiation failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to initiate payment. Please try again.';
      alert(errorMessage);
      setIsLoading(false);
    }
  };

  // Show loading spinner while checking authentication state or pricing data
  if (loading || pricingLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Transform dynamic plans to match component's expected structure
  const plans = dynamicPlans.map((plan) => {
    // Define color schemes based on plan name
    const colors = plan.name === 'BASIC' ? {
      headerBg: 'bg-[#04A7A7]',
      buttonBg: 'bg-[#04A7A7]',
      check: 'text-[#04A7A7]'
    } : plan.name === 'VECTOR PRO' ? {
      headerBg: 'bg-[#1D3557]',
      buttonBg: 'bg-[#1D3557]',
      check: 'text-[#1D3557]'
    } : {
      headerBg: 'bg-[#F46036]',
      buttonBg: 'bg-[#F46036]',
      check: 'text-[#F46036]'
    };

    // Transform features array to include 'included' property
    const features = plan.features.map(feature => ({
      text: feature,
      included: true
    }));

    return {
      name: plan.name,
      subtitle: plan.display_name,
      price: plan.original_price.toString(),
      promoPrice: plan.promo_price !== null && plan.promo_price !== undefined ? plan.promo_price.toString() : null,
      planType: 'MONTHLY PLAN',
      features,
      colors,
      discountPercent: activeOffer?.discount_percent || null
    };
  });


  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: isMobile ? 'none' : 'url("/assets/pricing/pricing_bg.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {!isMobileEmbed && <Navbar />}
      
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Pricing Section */}
        <section className={isMobileEmbed ? 'py-8 lg:py-8 relative' : 'py-16 lg:py-12 relative'}>
          <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
            {/* Section Header */}
            <div className="text-center mb-12">
              {/* Dynamic Promotional Banner - Only show if active offer exists */}
              {activeOffer && (
                <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-full inline-block mb-6">
                  <span className="font-bold text-lg">🔥 {activeOffer.name} 🔥</span>
                </div>
              )}
              
              {/* New User Free Trial Banner - Only show for users who haven't used trial */}
              {(() => {
                // Check if user has used trial (using same logic as button text)
                const hasUsedTrial = user?.has_used_trial || 
                  (trialStatus && trialStatus.has_used_trial);
                
                console.log('🔍 Banner visibility check:', {
                  userHasUsedTrial: user?.has_used_trial,
                  trialStatusHasUsedTrial: trialStatus?.has_used_trial,
                  shouldShowBanner: !hasUsedTrial
                });
                
                if (!hasUsedTrial) {
                  return (
                    <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-lg mb-6 max-w-4xl mx-auto">
                      <div className="flex items-center justify-center space-x-3">
                        <span className="text-2xl">🎉</span>
                        <div>
                          <h2 className="text-xl font-bold">New User Offer!</h2>
                          <p className="text-lg">Select any plan and get your extra 7 days completely free with full access to all EXPERT features.</p>
                        </div>
                        <span className="text-2xl">🎉</span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                Choose Your <span className="text-blue-600">Plan</span>
              </h1>
              {/*<p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Select the perfect plan that fits your investment needs and start your journey with TrendVector
              </p>*/}
            </div>

            {/* Pricing Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {plans.map((plan, index) => (
                <PricingCard
                  key={index}
                  plan={plan.name}
                  subtitle={plan.subtitle}
                  price={plan.price}
                  promoPrice={plan.promoPrice}
                  planType={plan.planType}
                  features={plan.features}
                  colors={plan.colors}
                  onGetNow={handlePayment}
                  isLoading={isLoading}
                  buttonText={getButtonText(plan.name)}
                  discountPercent={plan.discountPercent}
                />
              ))}
            </div>

            {/* Additional Information */}
            <div className="text-center mt-12">
              <p className="text-gray-600 text-sm">
                Upgrade anytime to unlock additional features.
              </p>
              {/* Dynamic promotional text - Only show if active offer exists */}
              {activeOffer && activeOffer.discount_percent && (
                <p className="text-red-600 text-sm font-semibold mt-2">
                  ⏰ Limited Time Offer: Get {activeOffer.discount_percent}% off on all plans! Offer ends soon.
                </p>
              )}
              <p className="text-gray-500 text-xs mt-2">
                * All prices are in Indian Rupees (INR). Plans are billed monthly.
              </p>
            </div>
          </div>
        </section>
      </div>

      {!isMobileEmbed && <Footer />}
    </div>
  );
};

export default PricingPage;
